import { ApolloClient, InMemoryCache } from "@apollo/client"
import { getGraphQLEndpoint } from "@pathwright/features/src/modules/pathwright/PathwrightClient"
import {
  constructUrl,
  getRelativeUrl,
  getSchoolUrl
} from "@pathwright/features/src/modules/utils/urls"
import { SchoolContextDocument } from "@pathwright/graphql-client/src/generated"
import { getIsLocalHost } from "@pathwright/ui/src/components/utils/env"
import { getSchoolEnvMismatch } from "@pathwright/utils/src/env/env"
import { z } from "zod"

// Loosely validating the school query param as a number or string.
const schoolQueryParamSchema = z.union([z.coerce.number().min(1), z.string()])

// Returns the kind and value of the school query param (id or url).
export function getSchoolQueryParam() {
  const { searchParams } = new URL(window.location.href)
  const schoolQueryParam = searchParams.get("school")

  const parsed = schoolQueryParamSchema.safeParse(schoolQueryParam)
  return parsed.success ? parsed.data : null
}

// Query the school by ID from the GraphQL API.
async function getSchoolById() {
  const schoolQueryParam = getSchoolQueryParam()

  if (schoolQueryParam && typeof schoolQueryParam === "number") {
    // Instantiate a basic Apollo Client, enough to query for school.
    const client = new ApolloClient({
      uri: `${getGraphQLEndpoint()}graphql?school_id=${schoolQueryParam}`,
      cache: new InMemoryCache()
    })

    // Query for school by ID.
    const result = await client.query({
      query: SchoolContextDocument
    })

    // Return the school URL.
    return result?.data?.school?.url
  }
}

export const transoformLocalConfigURL = function (configUrl) {
  // Ensure http for pathwrightdev urls.
  const protocol = /\.pathwrightdev\.com/.test(configUrl) ? "http" : "https"

  // Ensure protocol for all urls.
  const url = configUrl.replace(/(https*:\/\/|^)/, `${protocol}://`)

  // Return the complete url obj.
  return new URL(url)
}

export const getLocalConfigURL = function () {
  // Fallback to localStorage.
  const configUrl =
    getSchoolQueryParam() || localStorage.getItem("school_config_url")

  if (typeof configUrl !== "string") {
    alertMissingSchool()
    return null
  }

  const mismatch = getSchoolEnvMismatch(configUrl)
  if (mismatch) {
    alertSchoolEnvMismatch(configUrl, mismatch)
    return null
  }

  return transoformLocalConfigURL(configUrl)
}

// Explains why the client cannot load a school, and offers to reload with
// ?school= set to schoolUrl when one is known.
function promptSchoolReload(message, schoolUrl, fallbackStep) {
  const reloadUrl = schoolUrl
    ? `${window.location.origin}/?school=${schoolUrl}`
    : null
  const nextStep = reloadUrl ? `Press OK to open ${reloadUrl}` : fallbackStep

  if (confirm(`${message}\n\n${nextStep}`) && reloadUrl) {
    window.location.assign(reloadUrl)
  }
}

// Suggests a school on the backend this client targets.
function alertMissingSchool() {
  const defaultSchoolUrl = "paths.pathwrightstaging.com"
  const schoolUrl =
    getSchoolEnvMismatch(defaultSchoolUrl)?.matchingSchoolUrl ||
    defaultSchoolUrl

  promptSchoolReload(
    "Could not load Pathwright client. No school URL is set in the ?school= querystring or localStorage.",
    schoolUrl
  )
}

// The school and its stored session belong to another backend, and there is no
// way to tell which school on this backend was meant, so the developer must
// reload with one.
function alertSchoolEnvMismatch(configUrl, mismatch) {
  for (const key of ["school_config_url", "PW_SPACE", "auth_token"])
    localStorage.removeItem(key)

  const { schoolEnv, graphqlEnv, matchingSchoolUrl } = mismatch
  promptSchoolReload(
    `This client sends GraphQL requests to ${graphqlEnv}, but ${configUrl} is a ${schoolEnv} school. Cleared the stored school and auth token.`,
    matchingSchoolUrl,
    `Reload with ?school= set to a ${graphqlEnv} school.`
  )
}

// Gets the school config URL, querying by school ID if needed.
// When in local host, you can scope to a school (and possibly specific route)
// using these methods:
// 1. Querystring (ID): ?school=1
// 2. Querystring (URL): ?school=connect.pathwrightstaging.com
// 3. Querystring (URL + path): ?school=connect.pathwrightstaging.com/library/
// 4. Local Storage: school_config_url = connect.pathwrightstaging.com
export const getSchoolConfigURL = async function () {
  if (getIsLocalHost()) {
    const schoolURL = (await getSchoolById()) ?? getLocalConfigURL()?.origin
    // Boot reads the school config unconditionally, so hold bootstrap rather
    // than start the app without one.
    if (!schoolURL) await new Promise(() => {})
    return schoolURL
  } else {
    return getSchoolUrl()
  }
}

export const createSchoolUrl = getSchoolUrl

// optionally has initial forwardslash
const PRIVATE_API_RE = /(^(?!\/)|\/)api\/private\//
const LEGACY_API_RE = /(^(?!\/)|\/)api\/v1\//

export const getAPIUrl = function (relativeURL) {
  if (!PRIVATE_API_RE.test(relativeURL) && !LEGACY_API_RE.test(relativeURL)) {
    relativeURL = constructUrl("/api/private/", relativeURL)
  }
  return createSchoolUrl(getRelativeUrl(relativeURL))
}

export const encodeURLParams = function (obj) {
  let params = []
  for (let k in obj) {
    const v = obj[k]
    params.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
  }
  return (params = params.join("&"))
}

export const hasURLParameter = (param) => {
  const params = new URLSearchParams(window.location.search)
  return params.has(param)
}

export const getURLParameter = (param) => {
  const params = new URLSearchParams(window.location.search)
  return params.get(param)
}
